import { DailyWageReport, WeeklyReport } from '@/clients/omb-api';
import Api from '@/assets/ts/Api';
import { ReportsRepo } from '@/repos/ReportsRepo';
import { SaveReportResult } from '@/assets/ts/types/SaveReportResult';
import clone from 'clone';

export default class ReportsService {

    private readonly reportsRepo: ReportsRepo;

    private uploadingBatch = false;
    private readonly weeklyReportUploadListeners: ((report: WeeklyReport) => void)[] = [];
    private readonly dailyWageReportUploadListeners: ((report: DailyWageReport) => void)[] = [];

    constructor(reportsRepo: ReportsRepo) {
        this.reportsRepo = reportsRepo;
    }

    public async getWeeklyReport(reportId: number): Promise<WeeklyReport> {
        return this.reportsRepo.findWeeklyReportById(reportId);
    }

    public async getDailyWageReport(reportId: number): Promise<DailyWageReport> {
        return this.reportsRepo.findDailyWageReportById(reportId);
    }

    public async getWeeklyReportsByProjectGroup(projectGroupId: number): Promise<WeeklyReport[]> {
        let remoteReports: WeeklyReport[] = [];
        try {
            remoteReports = await Api.omb.getAllWeeklyReportsByProjectGroup(projectGroupId);
        } catch (e) {
            console.error('failed to fetch weekly reports from API', e);
        }

        const localReports = await this.reportsRepo.findAllWeeklyReportsByProjectGroup(projectGroupId);
        const mergedReports = this.mergeAndSortRemoteAndLocalReports(remoteReports, localReports);

        // Save remote reports, that are not saved locally, yet
        await this.reportsRepo.saveWeeklyReports(remoteReports.filter((report) => !localReports.find((r) => r.id === report.id)));

        return mergedReports;
    }

    public async getDailyWageReportsByProjectGroup(projectGroupId: number): Promise<DailyWageReport[]> {
        let remoteReports: DailyWageReport[] = [];
        try {
            remoteReports = await Api.omb.getAllDailyWageReportsByProjectGroup(projectGroupId);
        } catch (e) {
            console.error('failed to fetch daily wage reports from API', e);
        }

        const localReports = await this.reportsRepo.findAllDailyWageReportsByProjectGroup(projectGroupId);
        const mergedReports = this.mergeAndSortRemoteAndLocalReports(remoteReports, localReports);

        // Save remote reports, that are not saved locally, yet
        await this.reportsRepo.saveDailyWageReports(remoteReports.filter((report) => !localReports.find((r) => r.id === report.id)));

        return mergedReports;
    }

    public async getDailyWageReportsByRepair(repairId: number): Promise<DailyWageReport[]> {
        let remoteReports: DailyWageReport[] = [];
        try {
            remoteReports = await Api.omb.getAllDailyWageReportsByRepair(repairId);
        } catch (e) {
            console.error('failed to fetch daily wage reports from API', e);
        }

        const localReports = await this.reportsRepo.findAllDailyWageReportsByRepair(repairId);
        const mergedReports = this.mergeAndSortRemoteAndLocalReports(remoteReports, localReports);

        // Save remote reports, that are not saved locally, yet
        await this.reportsRepo.saveDailyWageReports(remoteReports.filter((report) => !localReports.find((r) => r.id === report.id)));

        return mergedReports;
    }

    private mergeAndSortRemoteAndLocalReports(remoteReports: (WeeklyReport | DailyWageReport)[], localReports: (WeeklyReport | DailyWageReport)[]): (WeeklyReport | DailyWageReport)[] {
        // Merge remote and local reports. Keep the later updated one if a report exists remotely and locally.
        let mergedReports = remoteReports.map((report) => {
            if (report.signature) return report;

            const local = localReports.find((r) => r.id === report.id);
            if (local) {
                if (local.signature) return local;

                const dateRemote = new Date(report.updatedAt || '');
                const dateLocal = new Date(local.updatedAt || '');
                if (dateLocal > dateRemote) return local;
            }

            return report;
        });

        // Add local reports, that do not exist remotely
        mergedReports = mergedReports.concat(localReports.filter((report) => {
            const existing = mergedReports.find((r) => r.id === report.id);
            return !existing;
        }));

        mergedReports.sort((a, b) => ((a.id || 0) > (b.id || 0) ? 1 : -1));

        return mergedReports;
    }

    private fixDateTypes(report: WeeklyReport | DailyWageReport): WeeklyReport | DailyWageReport {
        report.start = report.start ? new Date(report.start) : undefined;
        report.end = report.end ? new Date(report.end) : undefined;
        report.updatedAt = report.updatedAt ? new Date(report.updatedAt) : undefined;
        return report;
    }

    public async saveWeeklyReportLocal(report: WeeklyReport): Promise<SaveReportResult<WeeklyReport>> {
        report = this.fixDateTypes(report);
        report.updatedAt = new Date();
        const userId = localStorage.getItem('OMB_USER_ID');
        report.createdBy = userId ? parseInt(userId, 10) : undefined;

        const result: SaveReportResult<WeeklyReport> = { report };

        try {
            const saved = await this.reportsRepo.saveWeeklyReports([report]);
            const r = saved[0];
            result.report = r;
        } catch (e) {
            result.localError = e;
        }

        return result;
    }

    public async saveWeeklyReport(report: WeeklyReport, batch?: boolean): Promise<SaveReportResult<WeeklyReport>> {
        const result = await this.saveWeeklyReportLocal(report);

        if (this.uploadingBatch && !batch) {
            result.remoteError = new Error('Batch upload is running');
            return result;
        }

        try {
            report = this.fixDateTypes(report);
            const toSaveRemotely = clone(report);
            const r = await Api.omb.saveWeeklyReport(toSaveRemotely);
            r.offlineId = report.offlineId;
            result.report = r;
            this.notifyWeeklyReportUploadListeners(r);
        } catch (e) {
            result.remoteError = e;
        }

        try {
            await this.saveWeeklyReportLocal(result.report);
        } catch (e) {
            result.localError = e;
        }

        return result;
    }

    public async saveDailyWageReportLocal(report: DailyWageReport): Promise<SaveReportResult<DailyWageReport>> {
        report = this.fixDateTypes(report);
        report.updatedAt = new Date();
        const userId = localStorage.getItem('OMB_USER_ID');
        report.createdBy = userId ? parseInt(userId, 10) : undefined;

        const result: SaveReportResult<DailyWageReport> = { report };

        try {
            const saved = await this.reportsRepo.saveDailyWageReports([report]);
            const r = saved[0];
            result.report = r;
        } catch (e) {
            result.localError = e;
        }

        return result;
    }

    public async saveDailyWageReport(report: DailyWageReport, batch?: boolean): Promise<SaveReportResult<DailyWageReport>> {
        const result = await this.saveDailyWageReportLocal(report);

        if (this.uploadingBatch && !batch) {
            result.remoteError = new Error('Batch upload is running');
            return result;
        }

        try {
            report = this.fixDateTypes(report);
            const toSaveRemotely = clone(report);
            const r = await Api.omb.saveDailyWageReport(toSaveRemotely);
            r.offlineId = report.offlineId;
            result.report = r;
            this.notifyDailyWageReportUploadListeners(r);
        } catch (e) {
            result.remoteError = e;
        }

        try {
            await this.saveDailyWageReportLocal(result.report);
        } catch (e) {
            result.localError = e;
        }

        return result;
    }

    public async signWeeklyReport(report: WeeklyReport, signature: string): Promise<SaveReportResult<WeeklyReport>> {
        report.signature = signature;
        return this.saveWeeklyReport(report);
    }

    public async signDailyWageReport(report: DailyWageReport, signature: string): Promise<SaveReportResult<DailyWageReport>> {
        report.signature = signature;
        return this.saveDailyWageReport(report);
    }

    public async deleteWeeklyReport(report: WeeklyReport): Promise<void> {
        if (report.id) {
            await Api.omb.deleteWeeklyReport(report.id);
            await this.reportsRepo.deleteWeeklyReport(report.id);
        } else if (report.offlineId) {
            await this.reportsRepo.deleteWeeklyReportByOfflineId(report.offlineId);
        }
    }

    public async deleteDailyWageReport(report: DailyWageReport): Promise<void> {
        if (report.id) {
            await Api.omb.deleteDailyWageReport(report.id);
            await this.reportsRepo.deleteDailyWageReport(report.id);
        } else if (report.offlineId) {
            await this.reportsRepo.deleteDailyWageReportByOfflineId(report.offlineId);
        }
    }

    public async getLocalWeeklyReportsToUpload(): Promise<WeeklyReport[]> {
        const reports = await this.reportsRepo.findAllWeeklyReports();
        return reports.filter((report) => !!report.signature && !report.fileId);
    }

    public async getLocalDailyWageReportsToUpload(): Promise<DailyWageReport[]> {
        const reports = await this.reportsRepo.findAllDailyWageReports();
        return reports.filter((report) => !!report.signature && !report.fileId);
    }

    public addWeeklyReportUploadListener(listener: (report: WeeklyReport) => void): void {
        this.weeklyReportUploadListeners.push(listener);
    }

    public removeWeeklyReportUploadListener(listener: (report: WeeklyReport) => void): void {
        const index = this.weeklyReportUploadListeners.indexOf(listener);
        if (index > -1) {
            this.weeklyReportUploadListeners.splice(index, 1);
        }
    }

    public addDailyWageReportUploadListener(listener: (report: DailyWageReport) => void): void {
        this.dailyWageReportUploadListeners.push(listener);
    }

    public removeDailyWageReportUploadListener(listener: (report: DailyWageReport) => void): void {
        const index = this.dailyWageReportUploadListeners.indexOf(listener);
        if (index > -1) {
            this.dailyWageReportUploadListeners.splice(index, 1);
        }
    }

    private notifyWeeklyReportUploadListeners(report: WeeklyReport) {
        this.weeklyReportUploadListeners.forEach((listener) => listener(report));
    }

    private notifyDailyWageReportUploadListeners(report: DailyWageReport) {
        this.dailyWageReportUploadListeners.forEach((listener) => listener(report));
    }

    public async saveAllSignedOfflineReports(): Promise<void> {
        if (this.uploadingBatch) return;

        this.uploadingBatch = true;
        try {
            const weeklyReports = await this.getLocalWeeklyReportsToUpload();
            // eslint-disable-next-line no-restricted-syntax
            for (const report of weeklyReports) {
                // eslint-disable-next-line no-await-in-loop
                await this.saveWeeklyReport(report, true);
            }

            const dailyWageReports = await this.getLocalDailyWageReportsToUpload();
            // eslint-disable-next-line no-restricted-syntax
            for (const report of dailyWageReports) {
                // eslint-disable-next-line no-await-in-loop
                await this.saveDailyWageReport(report, true);
            }
        } finally {
            this.uploadingBatch = false;
        }
    }

}
